import { api } from './client'
import type { MarketSegment } from './nomenclatoare'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Comercial/PricingModels.cs ----------

export interface PriceListItem {
  id: string
  code: string
  name: string
  marketSegment: MarketSegment | null
  isActive: boolean
  entryCount: number
}

export interface PriceListDetail {
  id: string
  code: string
  name: string
  marketSegment: MarketSegment | null
  isActive: boolean
}

export type SavePriceListPayload = Omit<PriceListDetail, 'id' | 'isActive'>

export interface PriceListEntry {
  id: string
  articleId: string
  articleCode: string
  articleDescription: string
  price: number
  currency: string
  effectiveFrom: string
  effectiveTo: string | null
  reason?: string | null
}

export type SavePriceListEntryPayload = Omit<PriceListEntry, 'id' | 'articleCode' | 'articleDescription'>

export interface QuantityDiscountTier { id: string | null; minQuantity: number; discountPercent: number }
export interface ArticleQuantityDiscounts {
  articleId: string
  articleCode: string
  articleDescription: string
  tiers: QuantityDiscountTier[]
}

export interface PriceFormula {
  id: string
  articleId: string
  articleCode: string
  articleDescription: string
  rawMaterialPricePerKg: number
  aluWeightG: number | null
  aluPricePerKg: number | null
  conversionCostPer1000: number
  marginPer1000: number
  effectiveFrom: string
  effectiveTo: string | null
  computedPricePer1000: number | null
}

export type SavePriceFormulaPayload = Omit<PriceFormula, 'id' | 'articleCode' | 'articleDescription' | 'computedPricePer1000'>

export interface PriceBreakdownStep { label: string; amount: number | null }

export interface PriceCalculationResult {
  basePriceSource: string
  basePrice: number | null
  currency: string
  standardDiscountPercent: number | null
  skontoPercent: number | null
  quantityDiscountPercent: number | null
  netPrice: number | null
  cost: number | null
  marginAmount: number | null
  marginPercent: number | null
  belowMinMargin: boolean
  steps: PriceBreakdownStep[]
}

// ---------- liste de preț ----------

export const listPriceLists = () => api.get<PriceListItem[]>('/comercial/preturi/liste').then((r) => r.data)
export const getPriceList = (id: string) => api.get<PriceListDetail>(`/comercial/preturi/liste/${id}`).then((r) => r.data)
export const createPriceList = (payload: SavePriceListPayload) =>
  api.post<{ id: string }>('/comercial/preturi/liste', payload).then((r) => r.data)
export const updatePriceList = (id: string, payload: SavePriceListPayload) =>
  api.put(`/comercial/preturi/liste/${id}`, payload)
export const deactivatePriceList = (id: string) => api.post(`/comercial/preturi/liste/${id}/deactivate`)

export const listPriceListEntries = (listId: string) =>
  api.get<PriceListEntry[]>(`/comercial/preturi/liste/${listId}/preturi`).then((r) => r.data)
export const addPriceListEntry = (listId: string, payload: SavePriceListEntryPayload) =>
  api.post<{ id: string }>(`/comercial/preturi/liste/${listId}/preturi`, payload).then((r) => r.data)
export const updatePriceListEntry = (entryId: string, payload: SavePriceListEntryPayload) =>
  api.put(`/comercial/preturi/preturi/${entryId}`, payload)

// ---------- discount pe cantitate ----------

export const listQuantityDiscounts = () =>
  api.get<ArticleQuantityDiscounts[]>('/comercial/preturi/praguri-cantitate').then((r) => r.data)
export const saveQuantityDiscountTiers = (articleId: string, tiers: QuantityDiscountTier[]) =>
  api.put(`/comercial/preturi/praguri-cantitate/articol/${articleId}`, { tiers })

// ---------- formule de calcul preț ----------

export const listPriceFormulas = () => api.get<PriceFormula[]>('/comercial/preturi/formule').then((r) => r.data)
export const createPriceFormula = (payload: SavePriceFormulaPayload) =>
  api.post<{ id: string }>('/comercial/preturi/formule', payload).then((r) => r.data)
export const updatePriceFormula = (id: string, payload: SavePriceFormulaPayload) =>
  api.put(`/comercial/preturi/formule/${id}`, payload)

// ---------- calculator ----------

export const calculatePrice = (clientId: string, articleId: string, quantity: number) =>
  api.post<PriceCalculationResult>('/comercial/preturi/calculeaza', { clientId, articleId, quantity }).then((r) => r.data)
